import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  FiTrendingUp, FiUsers, FiClock, FiDollarSign, FiAlertTriangle,
  FiActivity, FiAward, FiCalendar, FiDownload, FiFilter, FiRefreshCw,
  FiMapPin, FiTrendingDown, FiCheckCircle, FiStar, FiTarget, FiBarChart2, FiZap
} from 'react-icons/fi';
import { HiOutlineSparkles, HiOutlineLightBulb } from 'react-icons/hi';
import { BsLightning } from 'react-icons/bs';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'];

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  
  console.log('AnalyticsDashboard component rendered');
  console.log('User:', user);
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [staffCostData, setStaffCostData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedView, setSelectedView] = useState('overview'); // overview, attendance, performance, costs, forecast
  const [topPerformersView, setTopPerformersView] = useState('month'); // week, month, quarter, custom
  const [topPerformersData, setTopPerformersData] = useState(null);
  const [topPerformersLoading, setTopPerformersLoading] = useState(false);
  const [forecastInsights, setForecastInsights] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  useEffect(() => {
    // Fetch top performers when component mounts or view changes
    fetchTopPerformers(topPerformersView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topPerformersView]);

  useEffect(() => {
    // Calculate forecast insights when component mounts
    calculateForecastInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      console.log('=== FETCHING ANALYTICS DATA ===');
      console.log('Period:', selectedPeriod);

      // Try to fetch analytics data, but don't fail if endpoints don't exist
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/analytics/attendance?period=${selectedPeriod}`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/performance`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/Employee-cost?period=${selectedPeriod}`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/compliance`, { headers }).catch(e => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/forecast`, { headers }).catch(e => ({ data: null })),
      ]);

      const [attendance, performance, staffCost, compliance, forecast] = results.map(r => 
        r.status === 'fulfilled' ? r.value : { data: null }
      );

      console.log('Attendance data:', attendance.data);
      console.log('Performance data:', performance.data);
      console.log('Staff cost data:', staffCost.data);
      console.log('Compliance data:', compliance.data);
      console.log('Forecast data:', forecast.data);

      setAttendanceData(attendance.data);
      setPerformanceData(performance.data);
      setStaffCostData(staffCost.data);
      setComplianceData(compliance.data);
      setForecastData(forecast.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTopPerformers = async (period, customStart = null, customEnd = null) => {
    try {
      setTopPerformersLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Calculate date range based on period
      let startDate, endDate;
      const now = new Date();
      
      if (period === 'custom' && customStart && customEnd) {
        startDate = customStart;
        endDate = customEnd;
      } else if (period === 'week') {
        const dayOfWeek = now.getDay();
        const monday = new Date(now);
        monday.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        startDate = monday.toISOString().split('T')[0];
        endDate = sunday.toISOString().split('T')[0];
      } else if (period === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      } else if (period === 'quarter') {
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        startDate = threeMonthsAgo.toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0];
      }
      
      console.log('=== FETCHING TOP PERFORMERS ===');
      console.log('Period:', period);
      console.log('Start Date:', startDate);
      console.log('End Date:', endDate);
      
      // Fetch schedules for the date range using the /range endpoint
      let allSchedules = [];
      try {
        const params = new URLSearchParams({
          startDate: startDate,
          endDate: endDate
        });
        const url = `${API_URL}/api/schedules/range?${params}`;
        console.log('Fetching from URL:', url);
        const schedulesRes = await axios.get(url, { headers });
        allSchedules = schedulesRes.data.data || [];
        console.log(`Fetched ${allSchedules.length} schedules from API`);
      } catch (err) {
        console.error('Error fetching schedules:', err.response?.data || err.message);
        setTopPerformersData({
          performers: [],
          period,
          startDate,
          endDate,
          totalSchedules: 0
        });
        return;
      }
      
      if (allSchedules.length === 0) {
        console.log('No schedules found for the period');
        setTopPerformersData({
          performers: [],
          period,
          startDate,
          endDate,
          totalSchedules: 0
        });
        return;
      }
      
      console.log('Total schedules fetched:', allSchedules.length);
      
      // Filter schedules by date range (already filtered by API, but double-check)
      const filteredSchedules = allSchedules.filter(schedule => {
        return schedule.date >= startDate && schedule.date <= endDate;
      });
      
      console.log('📊 Filtered schedules count:', filteredSchedules.length);
      
      // Aggregate by employee - handle careWorker as array
      const employeeStats = {};
      
      console.log('Starting aggregation...');
      
      filteredSchedules.forEach(schedule => {
        // careWorker is an array, so iterate through all workers on this shift
        const workers = Array.isArray(schedule.careWorker) ? schedule.careWorker : [schedule.careWorker];
        
        workers.forEach(worker => {
          if (!worker || !worker._id) return; // Skip if no worker assigned
          
          const empId = worker._id;
          
          if (!employeeStats[empId]) {
            employeeStats[empId] = {
              _id: empId,
              name: worker.name || `${worker.firstName || ''} ${worker.lastName || ''}`.trim() || 'Unknown',
              email: worker.emailId,
              avatar: worker.avatar,
              totalShifts: 0,
              totalHours: 0,
              locations: new Set()
            };
          }
          
          employeeStats[empId].totalShifts++;
          
          // Calculate hours for this shift using start and end fields
          if (schedule.start && schedule.end) {
            const [startHour, startMin] = schedule.start.split(':').map(Number);
            const [endHour, endMin] = schedule.end.split(':').map(Number);
            let hours = (endHour * 60 + endMin - startHour * 60 - startMin) / 60;
            if (hours < 0) hours += 24; // Handle overnight shifts
            
            // Subtract break time if present
            if (schedule.break) {
              hours -= schedule.break / 60;
            }
            
            employeeStats[empId].totalHours += Math.max(0, hours);
          }
          
          // Track unique locations
          if (schedule.location) {
            const locationId = schedule.location._id || schedule.location;
            employeeStats[empId].locations.add(locationId.toString());
          }
        });
      });
      
      // Convert to array and calculate score
      console.log('Total unique employees found:', Object.keys(employeeStats).length);
      
      const performers = Object.values(employeeStats).map(emp => ({
        ...emp,
        uniqueLocations: emp.locations.size,
        locations: Array.from(emp.locations),
        averageHoursPerShift: emp.totalShifts > 0 ? (emp.totalHours / emp.totalShifts).toFixed(1) : 0,
        score: (emp.totalShifts * 10) + (emp.totalHours * 2) + (emp.locations.size * 5) // Custom scoring
      })).sort((a, b) => b.score - a.score).slice(0, 10);
      
      console.log('🏆 Top 10 performers calculated:', performers.length);
      if (performers.length > 0) {
        console.log('Top performer:', performers[0]);
      }
      
      const finalData = {
        performers,
        period,
        startDate,
        endDate,
        totalSchedules: filteredSchedules.length
      };
      
      console.log('Setting top performers data:', finalData);
      setTopPerformersData(finalData);
      setTopPerformersLoading(false);
      console.log('Top performers data set successfully!');
    } catch (error) {
      console.error('ERROR fetching top performers:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response?.data);
      console.error('Error stack:', error.stack);
      
      // Set empty data so UI doesn't stay in loading state
      setTopPerformersData({
        performers: [],
        period,
        startDate: '',
        endDate: '',
        totalSchedules: 0
      });
      setTopPerformersLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const handleTopPerformersViewChange = (view) => {
    setTopPerformersView(view);
    if (view !== 'custom') {
      fetchTopPerformers(view);
    }
  };

  const handleApplyCustomDates = () => {
    if (customDateRange.start && customDateRange.end) {
      fetchTopPerformers('custom', customDateRange.start, customDateRange.end);
    }
  };

  // Calculate forecasting insights from historical schedule data
  const calculateForecastInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch last 3 months of data for analysis
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const today = new Date();

      const params = new URLSearchParams({
        startDate: threeMonthsAgo.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0]
      });

      const schedulesRes = await axios.get(`${API_URL}/api/schedules/range?${params}`, { headers });
      const allSchedules = schedulesRes.data.data || [];

      if (allSchedules.length === 0) {
        setForecastInsights(null);
        return;
      }

      // Group schedules by month and location
      const monthlyData = {};
      const locationData = {};
      const dayOfWeekData = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };

      allSchedules.forEach(schedule => {
        const date = new Date(schedule.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const dayOfWeek = date.getDay();

        // Monthly aggregation
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { shifts: 0, hours: 0, employees: new Set() };
        }
        monthlyData[monthKey].shifts++;
        
        // Calculate hours
        if (schedule.start && schedule.end) {
          const [startHour, startMin] = schedule.start.split(':').map(Number);
          const [endHour, endMin] = schedule.end.split(':').map(Number);
          let hours = (endHour * 60 + endMin - startHour * 60 - startMin) / 60;
          if (hours < 0) hours += 24;
          if (schedule.break) hours -= schedule.break / 60;
          monthlyData[monthKey].hours += Math.max(0, hours);
        }

        // Track employees
        const workers = Array.isArray(schedule.careWorker) ? schedule.careWorker : [schedule.careWorker];
        workers.forEach(worker => {
          if (worker?._id) monthlyData[monthKey].employees.add(worker._id);
        });

        // Location aggregation
        const locationName = schedule.location?.name || 'Unknown';
        if (!locationData[locationName]) {
          locationData[locationName] = { shifts: 0, hours: 0, trend: [] };
        }
        locationData[locationName].shifts++;

        // Day of week analysis
        dayOfWeekData[dayOfWeek]++;
      });

      // Calculate trends and predictions
      const monthlyKeys = Object.keys(monthlyData).sort();
      const recentMonths = monthlyKeys.slice(-3);
      
      // Average shifts per month
      const avgShiftsPerMonth = recentMonths.reduce((sum, key) => sum + monthlyData[key].shifts, 0) / recentMonths.length;
      const avgHoursPerMonth = recentMonths.reduce((sum, key) => sum + monthlyData[key].hours, 0) / recentMonths.length;
      
      // Trend calculation (growth rate)
      let trendPercentage = 0;
      if (recentMonths.length >= 2) {
        const oldestMonth = monthlyData[recentMonths[0]].shifts;
        const newestMonth = monthlyData[recentMonths[recentMonths.length - 1]].shifts;
        trendPercentage = ((newestMonth - oldestMonth) / oldestMonth) * 100;
      }

      // Next month prediction
      const nextMonthShifts = Math.round(avgShiftsPerMonth * (1 + trendPercentage / 100));
      const nextMonthHours = Math.round(avgHoursPerMonth * (1 + trendPercentage / 100));

      // Busiest day of week
      const busiestDay = Object.entries(dayOfWeekData).reduce((max, [day, count]) => 
        count > max.count ? { day: parseInt(day), count } : max, 
        { day: 0, count: 0 }
      );
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

      // Location insights
      const locationInsights = Object.entries(locationData)
        .map(([name, data]) => ({
          name,
          shifts: data.shifts,
          avgShiftsPerMonth: Math.round(data.shifts / 3),
          predictedNextMonth: Math.round((data.shifts / 3) * (1 + trendPercentage / 100))
        }))
        .sort((a, b) => b.shifts - a.shifts)
        .slice(0, 5);

      // Capacity recommendations
      const uniqueEmployees = new Set();
      recentMonths.forEach(key => {
        monthlyData[key].employees.forEach(emp => uniqueEmployees.add(emp));
      });
      const avgEmployeesNeeded = Math.ceil(uniqueEmployees.size);
      const recommendedStaff = Math.ceil(avgEmployeesNeeded * 1.15); // 15% buffer

      setForecastInsights({
        nextMonthPrediction: {
          shifts: nextMonthShifts,
          hours: nextMonthHours,
          trend: trendPercentage
        },
        locationForecasts: locationInsights,
        busiestDay: {
          day: dayNames[busiestDay.day],
          avgShifts: Math.round(busiestDay.count / 12) // 12 weeks in 3 months
        },
        staffingRecommendation: {
          current: avgEmployeesNeeded,
          recommended: recommendedStaff,
          buffer: recommendedStaff - avgEmployeesNeeded
        },
        historicalTrend: monthlyKeys.map(key => ({
          month: key,
          shifts: monthlyData[key].shifts,
          hours: Math.round(monthlyData[key].hours)
        }))
      });

      console.log('Forecast insights calculated');
    } catch (error) {
      console.error('Error calculating forecast insights:', error);
      setForecastInsights(null);
    }
  };

  const getColor = (index) => COLORS[index % COLORS.length];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Analytics Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Real-time workforce insights and performance metrics</p>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* Period Selector */}
              <select
                value={selectedPeriod}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">Last 3 Months</option>
              </select>

              {/* Refresh Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRefresh}
                disabled={refreshing}
                className={`px-4 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 ${
                  refreshing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
                }`}
              >
                <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </motion.button>
            </div>
          </div>

          {/* View Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {[
              { id: 'overview', label: 'Overview', icon: FiActivity },
              { id: 'attendance', label: 'Attendance', icon: FiCheckCircle },
              { id: 'performance', label: 'Performance', icon: FiAward },
              { id: 'costs', label: 'Payroll Analysis', icon: FiDollarSign },
              { id: 'forecast', label: 'Insights', icon: FiTrendingUp }
            ].map((view) => (
              <motion.button
                key={view.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedView(view.id)}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 whitespace-nowrap transition-all ${
                  selectedView === view.id
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300'
                }`}
              >
                <view.icon className="h-4 w-4" />
                {view.label}
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        <AnimatePresence mode="wait">
          {/* OVERVIEW VIEW */}
          {selectedView === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Attendance Rate */}
                <motion.div
                  whileHover={{ scale: 1.02, translateY: -4 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase">Attendance Rate</h3>
                    <div className="p-3 bg-green-100 rounded-lg">
                      <FiActivity className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-2">
                    {attendanceData?.summary?.attendanceRate || 0}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {attendanceData?.summary?.presentCount || 0} of {attendanceData?.summary?.totalRecords || 0} present
                  </p>
                </motion.div>

                {/* Punctuality Rate */}
                <motion.div
                  whileHover={{ scale: 1.02, translateY: -4 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase">Punctuality Rate</h3>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <FiClock className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-2">
                    {attendanceData?.summary?.punctualityRate || 0}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {attendanceData?.summary?.lateCount || 0} late arrivals
                  </p>
                </motion.div>

                {/* Staff Cost */}
                <motion.div
                  whileHover={{ scale: 1.02, translateY: -4 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase">Staff Cost</h3>
                    <div className="p-3 bg-emerald-100 rounded-lg">
                      <FiDollarSign className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-2">
                    £{(staffCostData?.summary?.totalPay || 0).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">
                    Avg £{staffCostData?.summary?.averageCostPerHour || 0}/hr
                  </p>
                </motion.div>

                {/* Compliance Rate */}
                <motion.div
                  whileHover={{ scale: 1.02, translateY: -4 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-600 uppercase">Compliance Rate</h3>
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <FiAlertTriangle className="h-6 w-6 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-2">
                    {complianceData?.summary?.complianceRate || 100}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {complianceData?.summary['non-compliant'] || 0} issues
                  </p>
                </motion.div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Daily Attendance Trend */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Attendance Trend</h3>
                  {attendanceData?.dailyTrend && attendanceData.dailyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={attendanceData.dailyTrend}>
                        <defs>
                          <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorLate" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="colorAbsent" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(date) => {
                            const d = new Date(date);
                            return `${d.getMonth() + 1}/${d.getDate()}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="present"
                          stackId="1"
                          stroke="#10B981"
                          fill="url(#colorPresent)"
                          name="Present"
                        />
                        <Area
                          type="monotone"
                          dataKey="late"
                          stackId="1"
                          stroke="#F59E0B"
                          fill="url(#colorLate)"
                          name="Late"
                        />
                        <Area
                          type="monotone"
                          dataKey="absent"
                          stackId="1"
                          stroke="#EF4444"
                          fill="url(#colorAbsent)"
                          name="Absent"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <FiCalendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No attendance data available</p>
                      </div>
                    </div>
                  )}
                </motion.div>

                {/* Staff Cost Breakdown */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Staff Cost Breakdown</h3>
                  {staffCostData?.summary && (staffCostData.summary.regularPay > 0 || staffCostData.summary.overtimePay > 0) ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Regular Pay', value: staffCostData.summary.regularPay },
                            { name: 'Overtime', value: staffCostData.summary.overtimePay },
                            { name: 'Double Time', value: staffCostData.summary.doubleTimePay },
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: £${entry.value.toFixed(0)}`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {COLORS.map((color, index) => (
                            <Cell key={`cell-${index}`} fill={color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => `£${value.toFixed(2)}`}
                          contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <FiDollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No cost data available</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Top Performers & At-Risk Employees */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Performers Leaderboard */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg p-6 text-white"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <FiAward className="h-6 w-6 mr-2" />
                      <h3 className="text-lg font-semibold">Top Performers</h3>
                    </div>
                    <div className="flex gap-2">
                      {['week', 'month', 'quarter'].map((view) => (
                        <button
                          key={view}
                          onClick={() => handleTopPerformersViewChange(view)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                            topPerformersView === view
                              ? 'bg-white text-blue-600'
                              : 'bg-white/20 text-white hover:bg-white/30'
                          }`}
                        >
                          {view === 'week' ? 'Week' : view === 'month' ? 'Month' : '3M'}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {/* Debug info */}
                    {console.log('Rendering top performers section')}
                    {console.log('Top performers loading:', topPerformersLoading)}
                    {console.log('Top performers data:', topPerformersData)}
                    {console.log('Performers array:', topPerformersData?.performers)}
                    {console.log('Performers length:', topPerformersData?.performers?.length)}
                    
                    {topPerformersLoading ? (
                      <div className="h-[300px] flex items-center justify-center">
                        <div className="text-center text-white">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-white mx-auto mb-4"></div>
                          <p>Loading top performers...</p>
                        </div>
                      </div>
                    ) : topPerformersData?.performers && topPerformersData.performers.length > 0 ? (
                      topPerformersData.performers.map((performer, index) => (
                        <motion.div
                          key={performer._id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * index }}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            index === 0
                              ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 border-yellow-300 text-gray-900'
                              : 'bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-base ${
                              index === 0 ? 'bg-yellow-600 text-white text-xl' :
                              index === 1 ? 'bg-gray-300 text-gray-700' :
                              index === 2 ? 'bg-orange-400 text-orange-900' :
                              'bg-white/30 text-white'
                            }`}>
                              {index === 0 ? '🏆' : index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-semibold truncate ${
                                index === 0 ? 'text-gray-900' : 'text-white'
                              }`}>
                                {performer.name}
                              </p>
                              <div className={`text-xs flex items-center gap-3 ${
                                index === 0 ? 'text-gray-700' : 'text-blue-100'
                              }`}>
                                <span>{performer.totalShifts} shifts</span>
                                <span>{Math.round(performer.totalHours)}h</span>
                                <span>{performer.uniqueLocations} loc</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            <p className={`text-xl font-bold ${
                              index === 0 ? 'text-gray-900' : 'text-white'
                            }`}>
                              {Math.round(performer.score)}
                            </p>
                            <p className={`text-xs ${
                              index === 0 ? 'text-gray-700' : 'text-blue-100'
                            }`}>Score</p>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="h-[300px] flex items-center justify-center">
                        <div className="text-center text-white/60">
                          <FiUsers className="h-12 w-12 mx-auto mb-2" />
                          <p>No performance data available</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {topPerformersData && (
                    <div className="mt-4 pt-4 border-t border-white/20 text-center text-xs text-blue-100">
                      Showing data from {new Date(topPerformersData.startDate).toLocaleDateString()} to {new Date(topPerformersData.endDate).toLocaleDateString()}
                    </div>
                  )}
                </motion.div>

                {/* Retention Risk Distribution */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Retention Risk Analysis</h3>
                  {performanceData?.byRiskLevel ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={[
                          { level: 'Low', count: performanceData.byRiskLevel.low, color: '#10B981' },
                          { level: 'Medium', count: performanceData.byRiskLevel.medium, color: '#F59E0B' },
                          { level: 'High', count: performanceData.byRiskLevel.high, color: '#EF4444' },
                          { level: 'Critical', count: performanceData.byRiskLevel.critical, color: '#991B1B' },
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="level" tick={{ fontSize: 12, fontWeight: 600 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        />
                        <Bar dataKey="count" name="Employees" radius={[8, 8, 0, 0]}>
                          {[
                            { level: 'Low', count: performanceData.byRiskLevel.low, color: '#10B981' },
                            { level: 'Medium', count: performanceData.byRiskLevel.medium, color: '#F59E0B' },
                            { level: 'High', count: performanceData.byRiskLevel.high, color: '#EF4444' },
                            { level: 'Critical', count: performanceData.byRiskLevel.critical, color: '#991B1B' },
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      <div className="text-center">
                        <FiAlertTriangle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No performance data available</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          )}

          {/* ATTENDANCE VIEW */}
          {selectedView === 'attendance' && (
            <motion.div
              key="attendance"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Employee Performance Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Employee Attendance Metrics</h3>
                    <p className="text-sm text-gray-500 mt-1">Detailed attendance and punctuality tracking</p>
                  </div>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700">
                    <FiDownload className="h-4 w-4" />
                    Export
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Employee</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Attendance</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Punctuality</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Late Count</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Absent Count</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Avg Late (mins)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {attendanceData?.byEmployee && attendanceData.byEmployee.length > 0 ? (
                        attendanceData.byEmployee.map((emp) => (
                          <tr key={emp.user._id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="font-medium text-gray-900">
                                {emp.user.firstName} {emp.user.lastName}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                emp.attendanceRate >= 95 ? 'bg-green-100 text-green-800' :
                                emp.attendanceRate >= 85 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {emp.attendanceRate}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                emp.punctualityRate >= 95 ? 'bg-green-100 text-green-800' :
                                emp.punctualityRate >= 85 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {emp.punctualityRate}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                              {emp.late || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                              {emp.absent || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                              {emp.averageLateMinutes || 0}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                            No employee attendance data available for this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* PERFORMANCE VIEW */}
          {selectedView === 'performance' && (
            <motion.div
              key="performance"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* At-Risk Employees Alert */}
              {performanceData?.atRiskEmployees && performanceData.atRiskEmployees.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                  <div className="flex items-center mb-4">
                    <FiAlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">At-Risk Employees</h3>
                    <span className="ml-auto bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold">
                      {performanceData.atRiskEmployees.length} employees need attention
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                    {performanceData.atRiskEmployees.map((employee) => (
                      <motion.div
                        key={employee._id}
                        whileHover={{ scale: 1.02 }}
                        className="flex flex-col p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200 hover:shadow-md transition-all"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-semibold text-gray-900">
                            {employee.user?.firstName} {employee.user?.lastName}
                          </p>
                          <div className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">
                            {employee.retentionRiskScore}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          Risk: <span className="font-semibold text-red-700 uppercase">{employee.retentionRiskLevel}</span>
                        </p>
                        {employee.retentionRiskFactors && employee.retentionRiskFactors.length > 0 && (
                          <ul className="text-xs text-gray-600 space-y-1">
                            {employee.retentionRiskFactors.slice(0, 2).map((factor, idx) => (
                              <li key={idx} className="flex items-start gap-1">
                                <span className="text-red-500 mt-0.5">•</span>
                                <span>{factor}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Performance Records */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">All Staff Performance Records</h3>
                  <p className="text-sm text-gray-500 mt-1">Comprehensive performance tracking and scoring</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Employee</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Overall Score</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Attendance Rate</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Risk Level</th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Risk Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {performanceData?.allPerformances && performanceData.allPerformances.length > 0 ? (
                        performanceData.allPerformances.map((perf) => (
                          <tr key={perf._id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="ml-0">
                                  <p className="font-medium text-gray-900">
                                    {perf.user?.firstName} {perf.user?.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">{perf.user?.emailId}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-2xl font-bold text-blue-600">{perf.overallScore || 0}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                (perf.metrics?.attendanceRate || 0) >= 95 ? 'bg-green-100 text-green-800' :
                                (perf.metrics?.attendanceRate || 0) >= 85 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {perf.metrics?.attendanceRate || 0}%
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                                perf.retentionRiskLevel === 'low' ? 'bg-green-100 text-green-800' :
                                perf.retentionRiskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                perf.retentionRiskLevel === 'high' ? 'bg-orange-100 text-orange-800' :
                                perf.retentionRiskLevel === 'critical' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {perf.retentionRiskLevel || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-gray-900">
                              {perf.retentionRiskScore || 0}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                            No performance records available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}

          {/* PAYROLL ANALYSIS VIEW */}
          {selectedView === 'costs' && (
            <motion.div
              key="costs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Cost Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <motion.div whileHover={{ scale: 1.02 }} className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
                  <h3 className="text-sm font-semibold uppercase mb-2 text-green-100">Total Payroll Cost</h3>
                  <p className="text-4xl font-bold">£{(staffCostData?.summary?.totalPay || 0).toLocaleString()}</p>
                  <p className="text-sm mt-2 text-green-100">For {selectedPeriod === 'week' ? 'this week' : selectedPeriod === 'month' ? 'this month' : 'last 3 months'}</p>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
                  <h3 className="text-sm font-semibold uppercase mb-2 text-blue-100">Average Cost Per Hour</h3>
                  <p className="text-4xl font-bold">£{staffCostData?.summary?.averageCostPerHour || 0}</p>
                  <p className="text-sm mt-2 text-blue-100">Across all staff members</p>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
                  <h3 className="text-sm font-semibold uppercase mb-2 text-purple-100">Total Hours Worked</h3>
                  <p className="text-4xl font-bold">{Math.round(staffCostData?.summary?.totalHours) || 0}h</p>
                  <p className="text-sm mt-2 text-purple-100">{staffCostData?.summary?.overtimeHours || 0} overtime hours</p>
                </motion.div>
              </div>

              {/* Cost Trend Chart */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payroll Cost Trend</h3>
                {staffCostData?.dailyTrend && staffCostData.dailyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={staffCostData.dailyTrend}>
                      <defs>
                        <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(date) => {
                          const d = new Date(date);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        formatter={(value) => `£${value.toFixed(2)}`}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="totalPay" stroke="#3B82F6" fill="url(#costGradient)" name="Total Cost" />
                      <Area type="monotone" dataKey="regularPay" stroke="#10B981" fill="none" strokeDasharray="5 5" name="Regular Pay" />
                      <Area type="monotone" dataKey="overtimePay" stroke="#F59E0B" fill="none" strokeDasharray="5 5" name="Overtime Pay" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[350px] flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <FiDollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No cost trend data available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Cost Per Location */}
              {staffCostData?.costPerLocation && staffCostData.costPerLocation.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Efficiency Per Location</h3>
                  <div className="space-y-4">
                    {staffCostData.costPerLocation.map((loc, index) => (
                      <div key={loc.location._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{loc.location.name}</p>
                            <p className="text-sm text-gray-500">{loc.shifts} shifts • {Math.round(loc.hours)} hours</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-blue-600">£{loc.totalPay.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">£{(loc.totalPay / loc.hours).toFixed(2)}/hr</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* INSIGHTS VIEW */}
          {selectedView === 'forecast' && (
            <motion.div
              key="forecast"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Top Performers Detail View with Custom Date */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      🏆 Top 10 Performers Leaderboard
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Based on shifts, hours worked, and location coverage</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleTopPerformersViewChange('week')}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        topPerformersView === 'week'
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => handleTopPerformersViewChange('month')}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        topPerformersView === 'month'
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => handleTopPerformersViewChange('quarter')}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        topPerformersView === 'quarter'
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Last 3 Months
                    </button>
                    <button
                      onClick={() => handleTopPerformersViewChange('custom')}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        topPerformersView === 'custom'
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Custom Range
                    </button>
                  </div>
                </div>

                {/* Custom Date Range Picker */}
                {topPerformersView === 'custom' && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={customDateRange.start}
                          onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={customDateRange.end}
                          onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={handleApplyCustomDates}
                        disabled={!customDateRange.start || !customDateRange.end}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}

                {/* Performance Chart */}
                {topPerformersData?.performers && topPerformersData.performers.length > 0 && (
                  <div className="mb-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={topPerformersData.performers}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45} 
                          textAnchor="end" 
                          height={100} 
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis yAxisId="left" orientation="left" stroke="#3B82F6" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" stroke="#10B981" tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalShifts" fill="#3B82F6" name="Shifts Completed" radius={[8, 8, 0, 0]} />
                        <Bar yAxisId="right" dataKey="totalHours" fill="#10B981" name="Hours Worked" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Leaderboard Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Rank</th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Employee</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase">Shifts</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase">Total Hours</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase">Avg Hours/Shift</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase">Locations</th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase">Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {topPerformersData?.performers && topPerformersData.performers.length > 0 ? (
                        topPerformersData.performers.map((performer, index) => (
                          <motion.tr
                            key={performer._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 * index }}
                            className={`hover:bg-gray-50 transition-colors ${
                              index === 0 ? 'bg-gradient-to-r from-yellow-50 to-amber-50' : ''
                            }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                                index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white text-2xl shadow-lg' :
                                index === 1 ? 'bg-gray-300 text-gray-700' :
                                index === 2 ? 'bg-orange-400 text-white' :
                                'bg-blue-100 text-blue-600'
                              }`}>
                                {index === 0 ? '🏆' : index + 1}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className={`font-semibold ${
                                  index === 0 ? 'text-yellow-900 text-lg' : 'text-gray-900'
                                }`}>
                                  {performer.name}
                                </p>
                                <p className="text-xs text-gray-500">{performer.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-bold text-blue-600">{performer.totalShifts}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-bold text-green-600">{Math.round(performer.totalHours)}h</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm font-medium text-gray-700">{performer.averageHoursPerShift}h</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                                {performer.uniqueLocations}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className={`text-2xl font-bold ${
                                index === 0 ? 'text-yellow-600' : 'text-blue-600'
                              }`}>
                                {Math.round(performer.score)}
                              </div>
                            </td>
                          </motion.tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                            <FiUsers className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                            <p className="text-lg">No performance data available for selected period</p>
                            <p className="text-sm mt-1">Try selecting a different date range</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* AI-POWERED FORECAST INSIGHTS */}
              {forecastInsights && (
                <>
                  {/* Next Month Prediction Cards */}
                  <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-xl shadow-lg p-8 text-white">
                    <div className="flex items-center mb-6">
                      <HiOutlineSparkles className="h-8 w-8 mr-3 text-purple-300" />
                      <div>
                        <h2 className="text-2xl font-bold">AI-Powered Demand Forecasting</h2>
                        <p className="text-indigo-100">Predictive analytics based on 3 months of historical data</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <FiCalendar className="h-6 w-6 text-indigo-200" />
                          <p className="text-sm text-indigo-100 font-medium">Next Month Prediction</p>
                        </div>
                        <p className="text-5xl font-bold mb-2">
                          {forecastInsights.nextMonthPrediction.shifts}
                        </p>
                        <p className="text-xs text-indigo-200">Expected shifts</p>
                        <div className={`mt-3 flex items-center gap-1 text-sm ${
                          forecastInsights.nextMonthPrediction.trend >= 0 ? 'text-green-300' : 'text-red-300'
                        }`}>
                          {forecastInsights.nextMonthPrediction.trend >= 0 ? 
                            <FiTrendingUp className="h-4 w-4" /> : 
                            <FiTrendingDown className="h-4 w-4" />
                          }
                          <span className="font-semibold">
                            {Math.abs(forecastInsights.nextMonthPrediction.trend).toFixed(1)}%
                          </span>
                          <span>{forecastInsights.nextMonthPrediction.trend >= 0 ? 'growth' : 'decline'}</span>
                        </div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <FiClock className="h-6 w-6 text-indigo-200" />
                          <p className="text-sm text-indigo-100 font-medium">Expected Hours</p>
                        </div>
                        <p className="text-5xl font-bold mb-2">
                          {forecastInsights.nextMonthPrediction.hours}
                        </p>
                        <p className="text-xs text-indigo-200">Total work hours needed</p>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <FiActivity className="h-6 w-6 text-indigo-200" />
                          <p className="text-sm text-indigo-100 font-medium">Busiest Day</p>
                        </div>
                        <p className="text-3xl font-bold mb-2">
                          {forecastInsights.busiestDay.day}
                        </p>
                        <p className="text-xs text-indigo-200">
                          Avg {forecastInsights.busiestDay.avgShifts} shifts/week
                        </p>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <FiUsers className="h-6 w-6 text-indigo-200" />
                          <p className="text-sm text-indigo-100 font-medium">Staff Needed</p>
                        </div>
                        <p className="text-5xl font-bold mb-2">
                          {forecastInsights.staffingRecommendation.recommended}
                        </p>
                        <p className="text-xs text-indigo-200">
                          +{forecastInsights.staffingRecommendation.buffer} buffer capacity
                        </p>
                      </motion.div>
                    </div>
                  </div>

                  {/* Location-Based Demand Forecast */}
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center mb-6">
                      <FiMapPin className="h-6 w-6 mr-2 text-purple-600" />
                      <h3 className="text-xl font-bold text-gray-900">Location-Based Shift Predictions</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Location Forecast Table */}
                      <div>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Location</th>
                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Avg/Month</th>
                                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Next Month</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {forecastInsights.locationForecasts.map((location, index) => (
                                <motion.tr
                                  key={location.name}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.05 * index }}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: getColor(index) }}></div>
                                      <span className="font-medium text-gray-900">{location.name}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="text-gray-700 font-semibold">{location.avgShiftsPerMonth}</span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">
                                      {location.predictedNextMonth}
                                    </span>
                                  </td>
                                </motion.tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Location Forecast Chart */}
                      <div>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={forecastInsights.locationForecasts}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="name" angle={-20} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip 
                              contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                            />
                            <Legend />
                            <Bar dataKey="avgShiftsPerMonth" fill="#8B5CF6" name="Current Avg" radius={[8, 8, 0, 0]} />
                            <Bar dataKey="predictedNextMonth" fill="#EC4899" name="Predicted" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Historical Trend Analysis */}
                  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center mb-6">
                      <FiBarChart2 className="h-6 w-6 mr-2 text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-900">Historical Trend & Growth Analysis</h3>
                    </div>
                    
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={forecastInsights.historicalTrend}>
                        <defs>
                          <linearGradient id="colorShifts" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                        />
                        <Legend />
                        <Line 
                          yAxisId="left" 
                          type="monotone" 
                          dataKey="shifts" 
                          stroke="#3B82F6" 
                          strokeWidth={3}
                          dot={{ fill: '#3B82F6', r: 5 }}
                          activeDot={{ r: 7 }}
                          name="Shifts"
                        />
                        <Line 
                          yAxisId="right" 
                          type="monotone" 
                          dataKey="hours" 
                          stroke="#10B981" 
                          strokeWidth={3}
                          dot={{ fill: '#10B981', r: 5 }}
                          activeDot={{ r: 7 }}
                          name="Hours"
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-1">
                          <HiOutlineLightBulb className="h-5 w-5 text-blue-700" />
                          <p className="text-sm text-blue-700 font-medium">Insight</p>
                        </div>
                        <p className="text-xs text-blue-600">
                          {forecastInsights.nextMonthPrediction.trend > 5 
                            ? 'Strong growth detected. Consider hiring more staff.'
                            : forecastInsights.nextMonthPrediction.trend < -5
                            ? 'Declining demand. Optimize resource allocation.'
                            : 'Stable demand pattern. Maintain current staffing levels.'
                          }
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-1">
                          <FiTarget className="h-5 w-5 text-green-700" />
                          <p className="text-sm text-green-700 font-medium">Action Item</p>
                        </div>
                        <p className="text-xs text-green-600">
                          Prepare for {forecastInsights.busiestDay.day}s - allocate {forecastInsights.busiestDay.avgShifts}+ employees.
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <FiBarChart2 className="h-5 w-5 text-purple-700" />
                          <p className="text-sm text-purple-700 font-medium">Capacity</p>
                        </div>
                        <p className="text-xs text-purple-600">
                          Current: {forecastInsights.staffingRecommendation.current} staff | 
                          Recommended: {forecastInsights.staffingRecommendation.recommended} staff
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Forecast Summary - Fallback for old API data */}
              {forecastData?.forecast && (
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-8 text-white">
                  <div className="flex items-center mb-6">
                    <FiTrendingUp className="h-8 w-8 mr-3" />
                    <div>
                      <h2 className="text-2xl font-bold">Business Insights & Demand Forecasting</h2>
                      <p className="text-indigo-100">AI-powered predictions based on historical schedule data</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">📅</span>
                        <p className="text-sm text-indigo-100 font-medium">Expected Shifts Next Month</p>
                      </div>
                      <p className="text-5xl font-bold mb-2">
                        {forecastData.forecast.nextMonth?.expectedShifts || 'N/A'}
                      </p>
                      <p className="text-xs text-indigo-200">Based on 6-month trend analysis</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">👥</span>
                        <p className="text-sm text-indigo-100 font-medium">Staff Required</p>
                      </div>
                      <p className="text-5xl font-bold mb-2">
                        {forecastData.forecast.nextMonth?.expectedWorkers || 'N/A'}
                      </p>
                      <p className="text-xs text-indigo-200">Estimated workforce needed</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">💡</span>
                        <p className="text-sm text-indigo-100 font-medium">AI Recommendation</p>
                      </div>
                      <p className="text-lg font-bold leading-tight mb-2">
                        {forecastData.forecast.recommendation}
                      </p>
                      <p className="text-xs text-indigo-200">Strategic action suggested</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Monthly Trend */}
              {forecastData?.monthlyTrend && forecastData.monthlyTrend.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Historical Monthly Trend</h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={forecastData.monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="totalShifts" stroke="#3B82F6" strokeWidth={2} name="Total Shifts" />
                      <Line yAxisId="right" type="monotone" dataKey="totalWorkers" stroke="#10B981" strokeWidth={2} name="Total Workers" />
                      <Line yAxisId="right" type="monotone" dataKey="uniqueLocations" stroke="#F59E0B" strokeWidth={2} name="Unique Locations" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
